<script setup>
import {
	ref,
	defineProps,
	onBeforeUnmount,
	onMounted,
	watch,
	nextTick,
	computed,
	getCurrentInstance,
} from 'vue';
import { $api } from '@/services';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { _var } from '@/enums/Common';
import UpdateModal from './UpdateModal.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import Loading from '@/components/common/Loading.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const appInstance = getCurrentInstance();
const teacherShortName =
	appInstance?.proxy?.$filters?.terminology('teacher_short_name') || 'TLV';
const props = defineProps({
	wideView: {
		type: [Boolean, String],
		default: false,
	},
});
const isWideView = computed(
	() => props.wideView === true || props.wideView === 'true'
);
const MOBILE_POPUP_BREAKPOINT = 768;
const isNarrowScreen = ref(false);
const showMobileList = ref(false);
const syncNarrowScreen = () => {
	if (typeof window === 'undefined') return;
	isNarrowScreen.value = window.innerWidth < MOBILE_POPUP_BREAKPOINT;
};
const isMobilePopup = computed(() => !isWideView.value && isNarrowScreen.value);
const listChatGroup = ref([]);
const lucaAssitant = ref({});
const chatGroup = ref({});
const isAssitant = ref(false);
const chatBodyRef = ref(null);
const messageInputRef = ref(null);
const groupId = ref(null);
const message = ref('');
const studentInfo = ref({});
const showStudentContext = ref(false);
const isOpen = ref(false);
const type = ref(null);
const centerName = computed(() => store.state.profile.current_org.name);
const dataCompleted = ref({});
const orderedMessages = computed(() =>
	[...(chatGroup.value?.messages || [])].reverse()
);
const inputSearch = ref(false);
const searchQuery = ref('');
const typing = ref(null);
const selectedId = ref(null);
const enableSummary = ref(false);
const isLoading = ref(false);
const showContext = ref(false);
const chatLoading = ref(false);
let activeSocket = null;
const ASSISTANT_INPUT_MIN_HEIGHT = 72;
const ASSISTANT_INPUT_MAX_HEIGHT = 384;
const STUDENT_INPUT_MAX_HEIGHT = 220;
const listQuestion = ref([
	'Làm sao để tăng động lực cho trẻ trong giờ trị liệu?',
	'Khi trẻ không hợp tác trong giờ học, nên xử lý thế nào?',
	'Dấu hiệu cho thấy mục tiêu hiện tại cần điều chỉnh là gì?',
	'Khi trẻ né tránh nhiệm vụ, nên tiếp cận lại ra sao?',
	'Khi trẻ tiến bộ chậm ở một lĩnh vực, nên rà soát điều gì trước?',
	'Cách xác định mức độ hỗ trợ phù hợp cho từng mục tiêu?',
	'Làm sao để tăng tương tác hai chiều trong giờ học?',
	'Khi trẻ dễ mất tập trung, nên điều chỉnh hoạt động thế nào?',
	'Dấu hiệu cho thấy trẻ đã sẵn sàng nâng mức độ mục tiêu?',
	'Khi trẻ có hành vi bùng nổ, nên xử lý và phòng ngừa ra sao?',
]);

const listStudentQuestion = ref([
	'Gợi ý 3 kỹ năng tiền đề (Prerequisite) trẻ cần có để vượt qua các khó khăn hiện tại',
	'Thiết kế một hoạt động giúp kết hợp lĩnh vực tiến bộ và lĩnh vực còn yếu của trẻ',
	'Với chẩn đoán của trẻ, các mục tiêu hiện tại đã thực sự phù hợp chưa?',
	`Trẻ ở độ tuổi này thường có những nhu cầu tâm lý gì mà ${teacherShortName} cần lưu ý thêm?`,
	'Dựa trên các tiến bộ nổi bật, gợi ý 5 mục tiêu tiếp theo phù hợp cho trẻ',
	'Với định hướng can thiệp hiện tại, mình nên ưu tiên kỹ năng nào trong tháng tới?',
	'Phân tích lý do trẻ vẫn gặp khó khăn ở các mục tiêu được nêu trong tóm tắt.',
	'Gợi ý các chiến lược hỗ trợ (Prompting) phù hợp cho giai đoạn can thiệp hiện tại của trẻ.',
	'Liệt kê các rào cản về giác quan trẻ có thể gặp phải dựa trên tình trạng chẩn đoán hiện tại.',
]);

const pickRandomQuestions = (items, count) => {
	const copy = [...items];
	for (let i = copy.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[copy[i], copy[j]] = [copy[j], copy[i]];
	}
	return copy.slice(0, Math.min(count, copy.length));
};

const randomListQuestions = computed(() =>
	pickRandomQuestions(listQuestion.value, 3)
);

const randomListStudentQuestions = computed(() =>
	pickRandomQuestions(listStudentQuestion.value, 3)
);
const scrollToLatestMessage = async () => {
	await nextTick();
	if (chatBodyRef.value) {
		chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
	}
};

const autoResizeMessageInput = () => {
	if (!messageInputRef.value) return;
	const inputEl = messageInputRef.value;
	const text = (message.value || '').trim();

	inputEl.style.setProperty('height', 'auto', 'important');

	if (isAssitant.value) {
		inputEl.style.setProperty(
			'min-height',
			`${ASSISTANT_INPUT_MIN_HEIGHT}px`,
			'important'
		);
		inputEl.style.setProperty(
			'max-height',
			`${ASSISTANT_INPUT_MAX_HEIGHT}px`,
			'important'
		);
		const contentHeight = inputEl.scrollHeight;
		const nextHeight = !text
			? ASSISTANT_INPUT_MIN_HEIGHT
			: Math.min(
					Math.max(contentHeight, ASSISTANT_INPUT_MIN_HEIGHT),
					ASSISTANT_INPUT_MAX_HEIGHT
			  );
		inputEl.style.setProperty('height', `${nextHeight}px`, 'important');
		inputEl.style.overflowY =
			contentHeight > ASSISTANT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
		inputEl.scrollTop = inputEl.scrollHeight;
		return;
	}

	inputEl.style.removeProperty('min-height');
	inputEl.style.removeProperty('max-height');
	const contentHeight = inputEl.scrollHeight;
	const nextHeight = Math.min(contentHeight, STUDENT_INPUT_MAX_HEIGHT);
	inputEl.style.height = `${nextHeight}px`;
	inputEl.style.overflowY =
		contentHeight > STUDENT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
	inputEl.scrollTop = inputEl.scrollHeight;
};

const resetMessageInputHeight = () => {
	if (!messageInputRef.value) return;
	const inputEl = messageInputRef.value;

	if (isAssitant.value) {
		inputEl.style.setProperty(
			'min-height',
			`${ASSISTANT_INPUT_MIN_HEIGHT}px`,
			'important'
		);
		inputEl.style.setProperty(
			'max-height',
			`${ASSISTANT_INPUT_MAX_HEIGHT}px`,
			'important'
		);
		inputEl.style.setProperty(
			'height',
			`${ASSISTANT_INPUT_MIN_HEIGHT}px`,
			'important'
		);
		inputEl.style.overflowY = 'hidden';
		return;
	}

	inputEl.style.removeProperty('min-height');
	inputEl.style.removeProperty('max-height');
	inputEl.style.height = 'auto';
	inputEl.style.overflowY = 'hidden';
};

onMounted(() => {
	syncNarrowScreen();
	window.addEventListener('resize', syncNarrowScreen);
	getListChatGroup();
});

onBeforeUnmount(() => {
	if (typeof window === 'undefined') return;
	window.removeEventListener('resize', syncNarrowScreen);
	closeActiveSocket();
});

watch(
	() => chatGroup.value?.messages?.length,
	() => {
		scrollToLatestMessage();
	}
);

watch(
	() => isAssitant.value,
	async () => {
		await nextTick();
		resetMessageInputHeight();
	}
);
watch(isMobilePopup, (value) => {
	if (!value) showMobileList.value = false;
});
watch(selectedId, () => {
	if (selectedId.value && !isAssitant.value) {
		getCompletedGoalOfSkillStats(selectedId.value);
	}
});
const getListChatGroup = async (data) => {
	try {
		console.log('data', data);
		const res = await $api.chat.listChatGroup(data);
		console.log('res', res);
		listChatGroup.value = data ? res.data.data : res.data.data.slice(1);
		if (!data) lucaAssitant.value = res.data.data[0];
		if (!data) {
			if (route.query.id) {
				getStudentChatGroup(route.query.id);
			} else getChatGroup(lucaAssitant.value.id);
		}
	} catch (error) {
		console.log(error);
	}
};

const searching = async () => {
	clearInterval(typing.value);
	typing.value = setTimeout(() => {
		getListChatGroup(searchQuery.value);
	}, 300);
};
const getTime = (time, returnDate = false) => {
	const date = new Date(time);
	const browserOffsetMinutes = -new Date().getTimezoneOffset();
	const localDate = new Date(
		date.getTime() + browserOffsetMinutes * 60 * 1000
	);
	const hours = localDate.getHours();
	const minutes = localDate.getMinutes();
	if (returnDate) {
		return `${localDate.getDate()}/${
			localDate.getMonth() + 1
		}/${localDate.getFullYear()} ${hours}:${minutes
			.toString()
			.padStart(2, '0')}`;
	}
	return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const getChatGroup = async (id) => {
	try {
		showMobileList.value = false;
		if (selectedId.value === id) return;
		message.value = '';
		chatLoading.value = true;
		selectedId.value = id;
		if (route.query.id && isWideView.value) {
			const nextQuery = { ...route.query };
			delete nextQuery.id;
			router.replace({ query: nextQuery });
		}
		studentInfo.value = {};
		showStudentContext.value = false;
		groupId.value = id;
		isAssitant.value = true;
		const res = await $api.chat.getChatGroup(id);
		chatGroup.value = res.data.data;
		setupWebSocket(true);
	} catch (error) {
		console.log(error);
	} finally {
		chatLoading.value = false;
	}
};

const getStudentChatGroup = async (id) => {
	try {
		showMobileList.value = false;
		if (selectedId.value === id) return;
		message.value = '';
		chatLoading.value = true;
		selectedId.value = id;
		if (route.query.id !== String(id) && isWideView.value) {
			router.replace({
				query: {
					...route.query,
					id: String(id),
				},
			});
		}
		isAssitant.value = false;
		showContext.value = false;
		const res = await $api.chat.getStudentChatGroup(id);
		chatGroup.value = res.data.data;
		groupId.value = chatGroup.value.chat_group.id;
		studentInfo.value = chatGroup.value.chat_group.student;
		if (chatGroup.value.student_context) showStudentContext.value = true;
		else showStudentContext.value = false;
		setupWebSocket(true);
	} catch (error) {
		console.log(error);
	} finally {
		chatLoading.value = false;
	}
};

const sendMessage = async () => {
	try {
		const text = message.value.trim();
		if (!text) return;
		message.value = '';
		await nextTick();
		resetMessageInputHeight();
		chatGroup.value.messages.unshift({
			text: text,
			sender_id: account_id,
			inserted_at: new Date().toISOString(),
		});
		chatGroup.value.messages.unshift({
			message_type: 'loading',
		});
		await $api.chat.sendMessage({
			text,
			chat_group_id: groupId.value,
		});
	} catch (error) {
		if (error?.response?.data?.errors.error === 'AI_CHAT_LIMIT_REACHED') {
			store.dispatch('setAlertMessage', {
				message: 'Bạn đã đạt giới hạn trao đổi của Trợ lý Luca AI',
				type: 'danger',
			});
		} else {
			store.dispatch('setAlertMessage', {
				message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
				type: 'danger',
			});
		}
	}
};

const clickQuestion = (value) => {
	if (
		chatGroup.value.student_context?.should_regenerate ||
		!chatGroup.value?.limit_ai_chat?.can_chat
	)
		return;
	message.value = value;
	sendMessage();
};

const generateStudentContext = async (data) => {
	try {
		isLoading.value = true;
		isOpen.value = false;
		setupWebSocket(false);
		if (isWideView.value) {
			showStudentContext.value = true;
			showContext.value = true;
		}
		await $api.chat.generateStudentContext(data);
		getStudentChatGroup(data);
		enableSummary.value = false;
	} catch (error) {
		console.log(error);
		isLoading.value = false;
	}
};

const access_token = localStorage.getItem(_var.TOKEN);
const account_id = store.state.profile.staff.account_id;
const backend_url = process.env.VUE_APP_URL;

const closeActiveSocket = () => {
	if (!activeSocket) return;

	activeSocket.onmessage = null;
	activeSocket.onopen = null;
	activeSocket.onerror = null;
	activeSocket.onclose = null;

	if (
		activeSocket.readyState === WebSocket.OPEN ||
		activeSocket.readyState === WebSocket.CONNECTING
	) {
		activeSocket.close();
	}

	activeSocket = null;
};

const setupWebSocket = (joinChat) => {
	closeActiveSocket();

	const socket_url = `${backend_url}/socket/websocket?token=${access_token}&role=teacher`;
	const socket = new WebSocket(socket_url);
	activeSocket = socket;
	const expectedChannel = joinChat
		? `bot_chat:${chatGroup.value.chat_group.id}`
		: `student_context:${studentInfo.value.id}`;

	socket.onmessage = (socketEvent) => {
		try {
			const incomingMessage = JSON.parse(socketEvent.data);
			const channel = incomingMessage.channel || incomingMessage.topic;
			const eventName = incomingMessage.event;

			if (channel !== expectedChannel) return;

			if (eventName === 'bot_chat_message_received') {
				if (chatGroup.value.messages[0].message_type === 'loading') {
					chatGroup.value.messages[0].message_type = 'message';
					chatGroup.value.messages[0].text =
						incomingMessage.payload?.data;
				} else {
					chatGroup.value.messages[0].text +=
						incomingMessage.payload?.data;
				}
				scrollToLatestMessage();
			}
			if (eventName === 'student_context_generated') {
				const chunk = incomingMessage.payload?.data ?? '';
				if (!chatGroup.value.student_context) {
					chatGroup.value.student_context = { context: '' };
				} else if (chatGroup.value.student_context.context == null) {
					chatGroup.value.student_context.context = '';
				}
				if (isLoading.value === true) {
					chatGroup.value.student_context.context = chunk;
					isLoading.value = false;
				} else {
					chatGroup.value.student_context.context += chunk;
				}
			}
		} catch (error) {
			console.error('Invalid websocket message:', error);
		}
	};

	socket.onopen = () => {
		const joinMessage = {
			topic: expectedChannel,
			event: 'phx_join',
			payload: {},
			ref: '1',
		};
		socket.send(JSON.stringify(joinMessage));
		console.log('WebSocket connection established');
	};
	socket.onerror = (error) => console.error('WebSocket error:', error);
	socket.onclose = () => {
		if (activeSocket === socket) activeSocket = null;
		console.log('WebSocket connection closed');
	};
};

const openUpdateStudentContext = () => {
	type.value = 'update';
	isOpen.value = true;
};

const updateStudentContext = async (studentContext) => {
	try {
		await $api.chat.updateStudentContext(
			studentInfo.value.id,
			studentContext
		);
		store.dispatch('setAlertMessage', {
			message: 'Bổ sung hồ sơ thành công',
			type: 'success',
		});
		isOpen.value = false;

		if (!chatGroup.value.student_context) {
			chatGroup.value.student_context = {};
		}
		if (!chatGroup.value.student_context.staff_student_context) {
			chatGroup.value.student_context.staff_student_context = {
				context: studentContext,
			};
		} else {
			chatGroup.value.student_context.staff_student_context.context =
				studentContext;
		}
	} catch (error) {
		console.log(error);
	}
};

const getCompletedGoalOfSkillStats = async (val) => {
	try {
		enableSummary.value = false;
		const res = await $api.chat.getStats(val);
		if (res?.data) {
			dataCompleted.value = res?.data?.data;
			if (
				dataCompleted.value.goal_count > 0 ||
				dataCompleted.value.skill_count > 0
			) {
				enableSummary.value = true;
			}
		}
	} catch (error) {
		console.log(error);
	}
};

const openCompletedGoalOfSkillStats = () => {
	type.value = 'completed';
	isOpen.value = true;
};

const openStudentContext = () => {
	if (isAssitant.value || !isWideView.value) return;
	showStudentContext.value = true;
};

const openMobileList = () => {
	if (!isMobilePopup.value) return;
	showMobileList.value = true;
};

const removeSearchQuery = () => {
	searchQuery.value = '';
	searching();
};
</script>
<template>
	<DaModalFrame v-model:open="isOpen">
		<UpdateModal
			:type="type"
			@close="isOpen = false"
			@submit="updateStudentContext"
			:dataCompleted="dataCompleted"
			@generate="generateStudentContext(studentInfo.id)"
			:context="
				chatGroup?.student_context?.staff_student_context?.context
			"
		/>
	</DaModalFrame>
	<div class="flex h-full overflow-hidden">
		<div
			v-show="!isMobilePopup || showMobileList"
			class="list-student h-full min-h-0 border-r-neutral-200 bg-white flex flex-col"
			:class="{ 'pl-6': isWideView }"
			:style="{
				minWidth: isMobilePopup
					? '100%'
					: !isWideView
					? '260px'
					: '360px',
				'border-top-left-radius': isMobilePopup
					? '0'
					: !isWideView
					? '8px'
					: '0',
				maxWidth: isMobilePopup ? '100%' : '360px',
			}"
		>
			<div class="sticky top-0 z-10 bg-white">
				<div class="p-4 flex items-center justify-between">
					<img
						class="h-8 w-auto"
						src="@/assets/images/icons/logo-ai.png"
						alt=""
					/>
					<div class="flex items-center gap-4">
						<img
							v-if="!isWideView"
							class="w-5 h-5 cursor-pointer"
							@click="inputSearch = !inputSearch"
							src="@/assets/images/icons/search.png"
							alt=""
						/>
						<img
							v-if="isMobilePopup"
							class="w-2.5 h-auto cursor-pointer"
							src="@/assets/images/icons/arrow-down.png"
							alt=""
							@click="$emit('close')"
						/>
					</div>
				</div>
				<div v-if="inputSearch || isWideView" class="px-4 pb-4">
					<div class="input-group">
						<input
							v-model="searchQuery"
							class="bg-white input-with-icon"
							placeholder="Tìm kiếm"
							type="text"
							@keyup="searching"
						/>
						<div class="input-icon w-3 h-3 z-20 cursor-pointer">
							<img
								v-if="searchQuery"
								@click="removeSearchQuery"
								src="@/assets/images/icons/x.png"
								alt=""
							/>
						</div>
					</div>
				</div>
			</div>
			<div class="flex-1 min-h-0 overflow-y-auto">
				<div
					class="p-4 flex items-center hover:bg-cream cursor-pointer"
					:class="{ 'bg-cream': selectedId == lucaAssitant.id }"
					@click="getChatGroup(lucaAssitant.id)"
				>
					<img
						src="@/assets/images/icons/bubble.png"
						alt=""
						class="w-9 h-9"
					/>
					<div class="ml-2 w-full overflow-hidden">
						<div class="flex items-center justify-between">
							<div class="text-sm font-semibold">
								Trợ lý Luca AI
							</div>
							<div
								class="text-neutral-400 text-xs"
								v-if="lucaAssitant.last_message?.inserted_at"
							>
								{{
									getTime(
										lucaAssitant.last_message?.inserted_at
									)
								}}
							</div>
						</div>
						<div class="text-neutral-700 text-sm truncate">
							{{ lucaAssitant.last_message?.text }}
						</div>
					</div>
				</div>
				<div class="px-4 py-2 font-semibold text-neutral-700 text-sm">
					Trao đổi theo hồ sơ học sinh
				</div>
				<div
					class="p-4 flex items-center border-b-neutral-200 cursor-pointer hover:bg-cream"
					:class="{ 'bg-cream': selectedId == item.student?.id }"
					@click="getStudentChatGroup(item.student?.id)"
					v-for="item in listChatGroup"
					:key="item"
				>
					<img
						v-if="item.student?.profile_photo?.w200"
						:src="item.student?.profile_photo?.w200"
						alt=""
						class="shadow-sm inline-block h-9 w-9 rounded-full object-cover grad-border"
					/>
					<span
						v-else
						class="inline-flex items-center justify-center h-9 w-9 rounded-full grad-border"
					>
						<span
							class="font-medium leading-none text-white text-xs"
							>{{
								item.student?.profile_photo?.default?.initial
							}}</span
						>
					</span>
					<div class="ml-2 w-full overflow-hidden flex-1">
						<div class="flex items-center justify-between">
							<div class="text-sm font-semibold">
								{{ item?.student?.name }}
							</div>
							<div
								class="text-neutral-400 text-sm"
								v-if="item.last_message?.inserted_at"
							>
								{{ getTime(item.last_message?.inserted_at) }}
							</div>
						</div>
						<div class="text-neutral-700 text-sm truncate">
							{{ item.last_message?.text }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			v-show="!isMobilePopup || !showMobileList"
			class="h-full min-h-0 flex flex-col relative flex-1 min-w-0"
			:class="{ 'border-r-neutral-200': showStudentContext }"
			:style="{
				'border-top-right-radius': isMobilePopup
					? '0'
					: !isWideView
					? '8px'
					: '0',
			}"
		>
			<div
				v-if="chatLoading"
				class="absolute inset-0 z-30 flex items-center justify-center bg-white/60"
			>
				<Loading />
			</div>
			<div
				class="p-3 flex items-center justify-between border-b-neutral-200"
				:class="{ 'bg-white': isWideView }"
				v-if="!chatLoading"
			>
				<div
					class="flex items-center gap-2"
					@click="openStudentContext"
				>
					<img
						v-if="isMobilePopup"
						src="@/assets/images/icons/arrow-right.png"
						alt=""
						class="w-4 h-4 cursor-pointer"
						style="transform: rotate(180deg)"
						@click.stop="openMobileList"
					/>
					<img
						v-if="isAssitant"
						class="w-10 h-10"
						src="@/assets/images/icons/bubble.png"
						alt=""
					/>
					<div v-else class="cursor-pointer">
						<img
							v-if="studentInfo?.profile_photo?.w200"
							:src="studentInfo?.profile_photo?.w200"
							alt=""
							class="shadow-sm inline-block h-9 w-9 rounded-full object-cover"
						/>
						<span
							v-else
							:style="{
								background:
									studentInfo?.profile_photo?.default?.color,
							}"
							class="inline-flex items-center justify-center h-9 w-9 rounded-full"
						>
							<span
								class="font-medium leading-none text-white text-xs"
								>{{
									studentInfo?.profile_photo?.default?.initial
								}}</span
							>
						</span>
					</div>
					<div class="text-neutral-700 font-semibold cursor-pointer">
						{{ studentInfo?.name || 'Trợ lý Luca AI' }}
					</div>
				</div>
				<div v-if="isWideView && !isAssitant">
					<img
						class="w-4 h-4 cursor-pointer"
						@click="openStudentContext"
						src="@/assets/images/icons/three-dots.png"
						alt=""
					/>
				</div>
				<div class="flex items-center gap-4" v-if="!isWideView">
					<img
						class="w-4 h-4 cursor-pointer"
						@click="
							$router.push(
								'/luca-ai-chat?id=' +
									(isAssitant
										? lucaAssitant.id
										: studentInfo.id)
							)
						"
						src="@/assets/images/icons/link.png"
						alt=""
					/>

					<img
						class="w-2.5 h-auto cursor-pointer"
						src="@/assets/images/icons/arrow-down.png"
						alt=""
						@click="$emit('close')"
					/>
				</div>
			</div>
			<div
				v-if="!chatLoading && !chatGroup?.limit_ai_chat?.can_chat"
				class="p-4 flex items-center justify-center bg-sienna-100 text-neutral-700 text-xs py-2 px-3"
			>
				Bạn còn <span class="font-semibold mx-1">0 tin nhắn</span> với
				Trợ lý AI Luca trong tháng này.
			</div>
			<div
				v-if="!chatLoading"
				class="flex-1 min-h-0 py-6 bg-gray-100 flex flex-col overflow-y-auto"
				ref="chatBodyRef"
				:style="{
					'max-width': !isWideView ? '440px' : '',
				}"
				:class="{ 'px-6': isWideView, 'px-3': !isWideView }"
			>
				<div
					v-if="
						chatGroup.enough_data_for_first_generation || isAssitant
					"
				>
					<div
						v-if="chatGroup?.messages?.length <= 1"
						class="pt-6"
						style="margin: auto"
						:style="{
							'max-width': !isWideView ? '375px' : '660px',
							'font-size': !isWideView ? '14px' : '16px',
						}"
					>
						<div
							class="text-neutral-700 font-semibold text-center mb-4 text-lg"
							v-if="isAssitant || !chatGroup.student_context"
						>
							Chào mừng bạn đến với Trợ lý Luca AI
						</div>
						<div
							class="text-neutral-700 font-semibold text-center mb-4 text-lg"
							v-else-if="chatGroup.student_context"
						>
							Luca AI đã sẵn sãng hỗ trợ bạn
						</div>
						<div class="text-neutral-700 text-center">
							<div class="mb-4" v-if="isAssitant">
								Đây là không gian để bạn trao đổi các câu hỏi
								xoay quanh giáo dục đặc biệt, phương pháp can
								thiệp và kiến thức chuyên môn, không gắn với một
								hồ sơ học sinh cụ thể.
							</div>
							<div
								class="mb-4"
								v-else-if="
									!chatGroup.student_context && !isAssitant
								"
							>
								Đây là không gian để bạn trao đổi trực tiếp về
								một học sinh cụ thể, dựa trên hồ sơ, kế hoạch
								can thiệp và tiến trình học tập của con.
							</div>
							<div
								class="mb-4"
								v-else-if="chatGroup.student_context"
							>
								Hồ sơ của trẻ đã được ghi nhớ và tóm tắt cho
								cuộc trò chuyện này.
							</div>
							<div
								v-if="isAssitant || chatGroup.student_context"
								style="
									padding-left: 16px;
									padding-right: 16px;
									width: 100%;
									box-sizing: border-box;
								"
							>
								<div class="mb-2">
									Bạn có thể hỏi Luca AI về:
								</div>
								<div
									class="flex items-stretch flex-col mb-4 gap-2"
								>
									<div
										class="mb-1 bg-white py-3 px-4 rounded-lg w-full cursor-pointer"
										v-for="item in isAssitant
											? randomListQuestions
											: randomListStudentQuestions"
										:key="item"
										@click="clickQuestion(item)"
										:style="{
											'max-width': !isWideView
												? '360px'
												: '100%',
										}"
									>
										{{ item }}
									</div>
								</div>
							</div>
							<div
								v-if="!isAssitant && !chatGroup.student_context"
								class="generate-context mb-4 rounded-lg overflow-hidden"
							>
								<div
									class="flex items-center gap-2 p-3 bg-yellow-100"
								>
									<img
										src="@/assets/images/icons/class-student.png"
										alt=""
										class="w-5 h-5"
									/>
									<div class="text-neutral-700 font-semibold">
										Tóm tắt hồ sơ của trẻ (bắt buộc)
									</div>
								</div>
								<div
									class="p-3 text-neutral-800 bg-white rounded-lg text-left"
								>
									<div class="mb-4">
										Để đảm bảo Luca AI hiểu đúng và đủ thông
										tin của học sinh trước khi trao đổi, hồ
										sơ của trẻ cần được tóm tắt trước khi
										bắt đầu.
									</div>
									<div class="mb-4">
										Luca AI sẽ ghi nhớ và sử dụng cho tất cả
										các cuộc trao đổi tiếp theo với học sinh
										này — các
										{{ $filters.terminology('teacher') }}
										khác không cần tóm tắt lại và không phát
										sinh thêm Luca Xu.
									</div>
									<div
										class="flex items-center justify-end gap-1 text-yellow-700 cursor-pointer font-semibold"
										@click="
											generateStudentContext(
												studentInfo?.id
											)
										"
									>
										<div>Tóm tắt hồ sơ</div>
										<img
											class="w-4 h-4"
											src="@/assets/images/icons/coin.png"
											alt=""
										/>
										<div>20</div>
									</div>
								</div>
							</div>
							<div
								class="mb-4 italic"
								v-if="!isAssitant && !chatGroup.student_context"
							>
								Sau khi hoàn tất tóm tắt, bạn có thể đặt câu hỏi
								và trao đổi trong cuộc trò chuyện này.
							</div>
							<div class="italic mb-4">
								💡 Mỗi câu trả lời từ Trợ lý Luca AI sử dụng 2
								Luca Xu.
							</div>
							<div class="mb-4 italic" v-if="isAssitant">
								Bạn có thể đặt câu hỏi ngay khi sẵn sàng.
							</div>
							<div v-if="isAssitant">
								Lưu ý: Để trao đổi về một học sinh cụ thể, bạn
								vui lòng chuyển sang cuộc trò chuyện riêng của
								học sinh đó để Trợ lý Luca AI có thể hỗ trợ
								chính xác hơn.
							</div>
							<div v-else>
								Lưu ý: Nếu bạn muốn trao đổi kiến thức không gắn
								với một hồ sơ học sinh cụ thể, vui lòng chuyển
								sang cuộc trò chuyện với Trợ lý Luca AI.
							</div>
						</div>
					</div>
					<div
						v-if="
							chatGroup?.messages?.length > 1 &&
							(isAssitant || chatGroup.student_context)
						"
						:style="{
							'font-size': !isWideView ? '14px' : '16px',
						}"
					>
						<div
							v-for="message in orderedMessages"
							:key="message.id"
						>
							<div
								v-if="message.message_type === 'system'"
								class="mt-4 text-center"
							>
								<div class="text-neutral-400 font-semibold">
									{{ getTime(message.inserted_at) }}
								</div>
								<div class="text-neutral-500">
									{{ message.text }}
								</div>
							</div>
							<div
								v-else-if="message.message_type === 'loading'"
								class="mt-4 z-20"
							>
								<Loading :position="'start'" />
							</div>
							<div
								class="user-chat mt-4"
								v-else-if="message.sender_id"
							>
								<div
									class="text-neutral-800"
									v-html="message.text"
								></div>
								<div
									class="text-neutral-800 text-xs text-right"
								>
									{{ getTime(message.inserted_at) }}
								</div>
							</div>
							<div
								class="assistant-chat mt-4"
								v-else
								:style="{
									'max-width': !isWideView ? '100%' : '',
								}"
							>
								<div
									class="text-neutral-800"
									v-html="
										message.text
											.replace(
												/^###\s+(.*)$/gm,
												'<strong>$1</strong>'
											)
											.replace(
												/\*\*(.*?)\*\*/g,
												'<b>$1</b>'
											)
											.replace(/^\*\s+/gm, '- ')
											.replace(/\n/g, '<br>')
									"
								></div>
							</div>
						</div>
					</div>
				</div>
				<div
					v-else
					class="text-center text-neutral-700"
					style="margin: auto"
					:style="{
						'max-width': !isWideView ? '375px' : '660px',
						'font-size': !isWideView ? '14px' : '16px',
					}"
				>
					<div class="text-lg font-semibold mb-2">
						Trợ lý Luca AI cần thêm dữ liệu về trẻ...
					</div>
					<div class="mb-4">
						Hiện tại, dữ liệu của bé vẫn chưa đủ để Trợ lý Luca AI
						hỗ trợ bạn. Để đưa ra các phân tích chuyên môn chính xác
						cho {{ studentInfo?.full_name }}, Luca AI sử dụng các
						Chẩn đoán, Kế hoạch và Báo cáo tháng có trên hệ thống.
					</div>
					<div class="mb-4">
						Bạn có thể cập nhật các thông tin trên cho trẻ hoặc nếu
						bạn muốn trao đổi kiến thức không gắn với một hồ sơ học
						sinh cụ thể, vui lòng chuyển sang cuộc trò chuyện riêng
						với Trợ lý Luca AI.
					</div>
					<div
						class="font-semibold bg-yellow-500 shadow-sm rounded py-3 px-4 cursor-pointer w-max mx-auto"
						@click="getChatGroup(lucaAssitant.id)"
					>
						Hỏi Trợ lý Luca AI về giáo dục đặc biệt
					</div>
				</div>
			</div>
			<div class="bg-gray-100 shrink-0" v-if="!chatLoading">
				<div
					v-if="!chatGroup?.limit_ai_chat?.can_chat"
					class="py-6"
					:class="{ 'px-4': isWideView, 'px-3': !isWideView }"
				>
					<div
						class="text-neutral-500 text-sm flex items-center gap-2"
					>
						<img
							src="@/assets/images/icons/warning.png"
							alt=""
							class="w-4 h-4"
						/>
						<div>
							Bạn đã đạt giới hạn tin nhắn với Trợ lý Luca AI
							trong tháng. Vui lòng liên hệ Quản lý cơ sở để được
							hỗ trợ hoặc điều chỉnh hạn mức.
						</div>
					</div>
				</div>
				<div
					class="pb-4"
					:class="{ 'px-4': isWideView, 'px-3': !isWideView }"
					v-else-if="
						(chatGroup?.student_context &&
							!chatGroup.student_context?.should_regenerate) ||
						isAssitant
					"
				>
					<div class="relative">
						<textarea
							ref="messageInputRef"
							class="w-full min-w-0 box-border text-neutral-700 shadow-lg area-input pr-14 resize-none overflow-hidden"
							:placeholder="
								isAssitant
									? 'Hỏi Trợ lý Luca AI về giáo dục đặc biệt...'
									: 'Hỏi Luca AI về can thiệp cho con…'
							"
							v-model="message"
							@input="autoResizeMessageInput"
							@keydown.enter.prevent="sendMessage"
							:class="{
								'pb-10': !isAssitant,
								'assistant-input': isAssitant,
							}"
						></textarea>
						<div
							v-if="message.length > 0"
							class="w-10 h-10 absolute bottom-4 right-4 cursor-pointer"
							@click="sendMessage"
						>
							<img
								src="@/assets/images/icons/send-message.png"
								alt=""
							/>
						</div>
						<div
							class="flex items-center gap-4 absolute bottom-4 left-3"
							v-if="!isAssitant"
						>
							<div
								v-if="
									(chatGroup.student_context
										?.should_regenerate ||
										enableSummary) &&
									isWideView
								"
								class="cursor-pointer text-neutral-700 text-xs flex items-center gap-2 bg-neutral-100 rounded-full px-2.5 py-1.5"
								@click="openCompletedGoalOfSkillStats"
							>
								<img
									src="@/assets/images/icons/dock-fill.png"
									alt=""
									class="w-4 h-4"
								/>
								<div>Tóm tắt hồ sơ</div>
							</div>
							<div
								v-if="chatGroup.student_context"
								@click="openUpdateStudentContext"
								class="cursor-pointer text-neutral-700 text-xs flex items-center gap-2 bg-neutral-100 rounded-full px-2.5 py-1.5"
							>
								<img
									src="@/assets/images/icons/plus-circle-fill.png"
									alt=""
									class="w-4 h-4"
								/>
								<div>Bổ sung hồ sơ</div>
							</div>
						</div>
					</div>
					<div class="text-neutral-500 text-xs text-center mt-1">
						Trợ lý Luca AI hỗ trợ chuyên môn dựa trên dữ liệu hiện
						có và không thay thế đánh giá chuyên môn trực tiếp của
						nhà trị liệu
					</div>
				</div>
				<div
					v-else-if="chatGroup.student_context?.should_regenerate"
					:class="{ 'px-4': isWideView, 'px-3': !isWideView }"
				>
					<div
						class="generate-context mb-8 rounded-lg overflow-hidden"
					>
						<div class="flex items-center gap-2 p-3 bg-yellow-100">
							<img
								src="@/assets/images/icons/class-student.png"
								alt=""
								class="w-4 h-4"
							/>
							<div class="text-neutral-700 font-semibold">
								Con đã có thêm nhiều thay đổi mới trong quá
								trình học
							</div>
						</div>
						<div class="p-3 text-neutral-800 bg-white">
							<div>• Có đánh giá phát triển mới</div>
							<div class="mb-4">
								• Có chẩn đoán mới / cập nhật chẩn đoán
							</div>
							<div class="mb-4">
								Để Luca AI tiếp tục đồng hành sát với tiến trình
								hiện tại của con, bạn vui lòng tóm tắt lại hồ sơ
								của trẻ trước khi trao đổi.
							</div>
							<div
								class="flex items-center justify-end gap-1 text-yellow-700 font-semibold cursor-pointer"
								@click="generateStudentContext(studentInfo?.id)"
							>
								<div>Tóm tắt hồ sơ của trẻ</div>
								<img
									class="w-5 h-5"
									src="@/assets/images/icons/coin.png"
									alt=""
								/>
								<div>20</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			class="bg-white flex flex-col h-full min-h-0"
			v-if="showStudentContext && isWideView"
			style="min-width: 360px; max-width: 360px"
		>
			<div class="flex-1 min-h-0 overflow-y-auto px-4 pt-8">
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-2">
						<img
							src="@/assets/images/icons/arrow-right.png"
							alt=""
							class="w-4 h-4 cursor-pointer"
							v-if="showContext"
							@click="showContext = false"
							style="transform: rotate(180deg)"
						/>
						<div class="text-neutral-700" v-if="showContext">
							<div class="font-semibold text-lg">
								Xem tóm tắt hồ sơ
							</div>
							<div class="text-sm" v-if="chatGroup?.student_context?.updated_at">
								{{
									getTime(
										chatGroup?.student_context?.updated_at,
										true
									)
								}}
							</div>
						</div>
					</div>
					<img
						v-if="!showContext"
						src="@/assets/images/icons/x.png"
						alt=""
						class="cursor-pointer"
						@click="showStudentContext = false"
					/>
				</div>
				<div
					v-if="!showContext"
					class="flex items-center justify-center flex-col py-6"
				>
					<div style="border: 2px #f1a267 solid" class="rounded-full">
						<img
							v-if="studentInfo?.profile_photo?.w200"
							:src="studentInfo?.profile_photo?.w200"
							alt=""
							class="shadow-sm inline-block h-24 w-24 rounded-full"
						/>
						<span
							v-else
							:style="{
								background:
									studentInfo?.profile_photo?.default?.color,
							}"
							class="inline-flex items-center justify-center h-24 w-24 rounded-full"
						>
							<span
								class="font-medium leading-none text-white text-4xl"
								>{{
									studentInfo?.profile_photo?.default?.initial
								}}</span
							>
						</span>
					</div>
					<div class="text-neutral-700 text-lg font-semibold mt-2">
						{{ studentInfo?.name }}
					</div>
					<div class="text-neutral-700 text-sm">
						{{ centerName }}
					</div>
				</div>
				<div v-if="!showContext">
					<div
						class="flex items-center justify-between text-neutral-600 cursor-pointer py-4"
						@click="
							$router.push(`/record/student/${studentInfo?.id}`)
						"
					>
						<div class="flex items-center gap-2">
							<img
								src="@/assets/images/icons/view-file.png"
								alt=""
								class="w-5 h-5"
							/>
							<div>Xem hồ sơ của trẻ</div>
						</div>
						<img
							src="@/assets/images/icons/arrow-right.png"
							alt=""
							class="w-4 h-4"
						/>
					</div>
					<div
						class="flex items-center justify-between text-neutral-600 cursor-pointer py-4"
						@click="showContext = true"
						v-if="chatGroup.student_context"
					>
						<div class="flex items-center gap-2">
							<img
								src="@/assets/images/icons/view-context.png"
								alt=""
								class="w-5 h-5"
							/>
							<div>Xem tóm tắt của trẻ</div>
						</div>
						<img
							src="@/assets/images/icons/arrow-right.png"
							alt=""
							class="w-4 h-4"
						/>
					</div>
				</div>
				<div v-if="showContext" class="mt-6">
					<div v-if="isLoading" class="text-neutral-700">
						<div>
							Trợ lý Luca AI đang đọc và tổng hợp hồ sơ của trẻ để
							hỗ trợ bạn tốt nhất.<br />
							Vui lòng chờ trong ít phút nhé
						</div>
						<div class="z-2 mt-8">
							<Loading />
						</div>
					</div>
					<div v-else>
						<div
							v-html="
								chatGroup?.student_context?.context
									.replace(/^###\s+(.*)$/gm, '<b>$1</b>')
									.replace(
										/\*\*(.*?)\*\*/g,
										'<div class=\'font-semibold\'>$1</div>'
									)
									.replace(/^\*\s+/gm, '- ')
									.replace(/\n/g, '<br>')
							"
						></div>
						<div
							v-if="
								chatGroup?.student_context
									?.staff_student_context?.context
							"
							class="text-neutral-700 mt-4"
						>
							<div class="font-semibold">
								Bổ sung của
								{{ $filters.terminology('teacher') }}:
							</div>
							<div
								v-html="
									chatGroup?.student_context?.staff_student_context?.context.replace(
										/\n/g,
										'<br>'
									)
								"
							></div>
						</div>
						<div class="text-neutral-700 mt-4">
							<div>---</div>
							<div>- Thông tin trên đã đúng và đầy đủ chưa?</div>
							<div>
								- Có lưu ý đặc biệt nào khi làm việc với trẻ mà
								bạn muốn Luca ghi nhớ thêm không?
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="shrink-0 px-4 py-4 ml-auto" v-if="showContext">
				<div class="luca-button" @click="openUpdateStudentContext">
					Bổ sung hồ sơ
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.border-b-neutral-200 {
	border-bottom: 1px solid #e1e7ef;
}
.border-b-neutral-200:last-child {
	border-bottom: none;
}
.border-r-neutral-200 {
	border-right: 1px solid #e1e7ef;
}
.area-input {
	border: 1px solid #f1f5f9 !important;
	border-radius: 16px !important;
	min-height: 103px;
}
.area-input:placeholder-shown {
	line-height: normal;
	padding-top: 24px;
	text-decoration: none !important;
}

.assistant-input {
	min-height: 72px !important;
	max-height: 384px !important;
	height: 72px !important;
	box-sizing: border-box;
}
.user-chat {
	background: #f0bb4f;
	border-top-left-radius: 10px;
	border-bottom-left-radius: 10px;
	border-top-right-radius: 10px;
	padding: 8px;
	max-width: 80%;
	margin-left: auto;
	width: fit-content;
	word-break: break-word;
	overflow-wrap: anywhere;
}
.assistant-chat {
	max-width: 66.67%;
	width: fit-content;
	margin-right: auto;
	word-break: break-word;
	overflow-wrap: anywhere;
}
.generate-context {
	border: 1px solid #f4c870;
}
.grad-border {
	border-radius: 9999px;
	padding: 2px;
	background: linear-gradient(148.76deg, #8a3bf5, #fe07d7);
	z-index: 100;
}

.grad-border > span {
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: inherit;
	background: #f1a267;
	width: 100%;
	height: 100%;
}
@media screen and (min-width: 1440px) {
	.assistant-chat {
		max-width: 1080px;
	}
	.list-student {
		width: 400px;
	}
}
@media screen and (min-width: 1080px) {
	.assistant-chat {
		max-width: 660px;
	}
	.list-student {
		width: 320px;
	}
}
</style>